import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Form, InputGroup, Dropdown } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { FaFileExport } from 'react-icons/fa';

import Wrapper from '../../components/Wrapper';
import SupportPagination from '../../components/utils/supportPagination';
import PartnerPagination from '../../components/utils/partnerPagination';
import ReportsTable from '../../components/tables/ReportsTable';

import axiosInstance from '../../utils/axios';
import { exportToCSV, exportToExcel, exportToPDF } from '../../components/utils/ExportReports';
import WaterReportUpdateModal from '../../components/modals/WaterReportUpdate';
import { getRole } from '../../utils/role';

import {
  fetchReports,
  setCurrentPage,
} from '../../store/features/reportsSlice';

import { fetchReport } from '../../store/features/reportSlice';

const Reports = () => {
  const dispatch = useDispatch();
  const role = getRole();

  const [searchTerm, setSearchTerm] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);

  // Client-side pagination state
  const [clientPage, setClientPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  const {
    reports,
    allReports,   
    currentPage,
    totalPages,
    status,
  } = useSelector((state) => state.reports);

  const [showWaterModal, setShowWaterModal] = useState(false);
  const [selectedWaterReport, setSelectedWaterReport] = useState(null);

  const handleOpenWaterModal = async (reportRow) => {
    try {
      Swal.fire({
        title: 'Fetching details...',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const resultAction = await dispatch(fetchReport({ id: reportRow.report_id }));

      if (fetchReport.fulfilled.match(resultAction)) {
        Swal.close();
        // resultAction.payload.report contains the full database row
        setSelectedWaterReport(resultAction.payload.report);
        setShowWaterModal(true);
      } else {
        Swal.fire('Error', resultAction.payload || 'Failed to fetch full report details', 'error');
      }
    } catch (error) {
      Swal.fire('Error', 'An unexpected error occurred while fetching', 'error');
    }
  };

  useEffect(() => {
    dispatch(
      fetchReports({
        page: currentPage,
        limit: 15,
        keyword:
          role === 'SUPPORT'
            ? submittedSearch || undefined
            : undefined,
      })
    );
  }, [dispatch, currentPage, submittedSearch]);

  /* SEARCH */
  const handleSearch = () => {
    if (role !== 'SUPPORT') return;

    setSubmittedSearch(searchTerm.trim());

    if (currentPage !== 1) {
      dispatch(setCurrentPage(1));
    }
  };

  const isClientSide = (role === 'PARTNER' || role === 'TEAM_MEMBER') || role === 'FIELD_OFFICER' || role === 'FARMER';
  
  // Filter & Paginate locally
  let tableData = reports;
  let totalClientPages = 0;

  let clientStartIndex = 0;
  if (isClientSide) {
      const filtered = allReports.filter((item) => 
        Object.values(item).some(val => 
            String(val).toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
      
      const indexOfLast = clientPage * itemsPerPage;
      const indexOfFirst = indexOfLast - itemsPerPage;
      clientStartIndex = indexOfFirst;
      tableData = filtered.slice(indexOfFirst, indexOfLast);
      totalClientPages = Math.ceil(filtered.length / itemsPerPage);
  }


  const updateReportLock = async (report_id, is_locked) => {
    try {
      await axiosInstance.post('/update-report-lock', {
        report_id,
        is_locked,
      });
      return true;
    } catch {
      return false;
    }
  };

  const handleLock = (index) => {
    const report = tableData[index]; 
    const action = report.is_locked ? 'Unlock' : 'Lock';

    Swal.fire({
      title: `${action} Report`,
      text: 'Are you sure you want to do this change?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
    }).then(async (result) => {
      if (result.isConfirmed) {
        await updateReportLock(
          report.report_id,
          !report.is_locked
        );
        dispatch(fetchReports({ page: currentPage, limit: 15, keyword: submittedSearch || undefined }));
        Swal.fire(
          'Success',
          `Report ${action}ed successfully.`,
          'success'
        );
      }
    });
  };


  const safeExport = async (exportFn) => {
    try {
      setIsExporting(true);
      await exportFn(isClientSide ? allReports : reports); 
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Wrapper>
      <div className="reports-container py-4 w-100">
        <div className="d-flex flex-column flex-md-row justify-content-between mb-3 w-100">
          <h1 className="fw-bolder">Reports</h1>

          <div className="d-flex justify-content-end align-items-center gap-2">
            {/* EXPORT */}
            {(reports.length > 0 || allReports.length > 0) && ['SUPPORT', 'PARTNER', 'FIELD_OFFICER'].includes(role) && (
              <Dropdown
                show={showExportDropdown}
                onToggle={() => setShowExportDropdown(!showExportDropdown)}
              >
                <Dropdown.Toggle
                  variant="success"
                  disabled={isExporting}
                >
                  <FaFileExport />{' '}
                  {isExporting ? 'Exporting...' : 'Export'}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => safeExport(exportToCSV)}>
                    Export to CSV
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => safeExport(exportToExcel)}>
                    Export to Excel
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => safeExport(exportToPDF)}>
                    Export to PDF
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            )}

            {/* SEARCH */}
            <InputGroup>
              <Form.Control
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => {
                    setSearchTerm(e.target.value);
                    if (isClientSide) setClientPage(1);
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              {role === 'SUPPORT' && (
                <Button onClick={handleSearch}>Search</Button>
              )}
            </InputGroup>
          </div>
        </div>

        {status === 'succeeded' || status === 'loading' ? (
          <>
            <ReportsTable
              reports={tableData}
              handleLock={handleLock}
              startIndex={isClientSide ? clientStartIndex : 0}
              handleOpenWaterModal={handleOpenWaterModal}
            />

            {/* Pagination */}
            {role === 'SUPPORT' ? (
                <SupportPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  setCurrentPage={setCurrentPage}
                  fetchData={fetchReports}
                  searchKeyword={searchTerm}
                />
            ) : (
                <PartnerPagination 
                    currentPage={clientPage}
                    totalPages={totalClientPages}
                    onPageChange={setClientPage}
                    onItemsPerPageChange={setItemsPerPage}
                />
            )}
          </>
        ) : (
          <h3>Loading...</h3>
        )}
      </div>
      {selectedWaterReport && (
        <WaterReportUpdateModal
          show={showWaterModal}
          handleClose={() => setShowWaterModal(false)}
          report={selectedWaterReport}
          onSuccess={() => dispatch(fetchReports({ page: currentPage, limit: 15, keyword: submittedSearch || undefined }))}
        />
      )}
    </Wrapper>
  );
};

export default Reports;